/**
 * PERCURSO — a tela de quem responde.
 *
 * O que ela procura mostrar:
 *
 *  1. **Um percurso, não uma lista.** As questões montadas pelo professor, em
 *     sequência, com progresso visível e um fecho.
 *  2. **O retorno é o erro NOMEADO.** Depois de responder, o aluno não lê "incorreto":
 *     lê o nome do erro que cometeu — e pode abrir o COMENTÁRIO DETALHADO, com a conta
 *     que o levou à resposta e a conta certa, com os números da questão que ele viu.
 *  3. **O fecho vira revisão.** A tela final lista cada questão; qualquer uma pode ser
 *     reaberta, do jeito que foi respondida, com o comentário ao lado. E os erros
 *     distintos viram a pauta do que estudar.
 *
 * O que ela NÃO é: não há contas, turmas nem seleção adaptativa. A ordem é a que o
 * professor escolheu em "Montar percurso".
 */

import { getTemplate, hasTemplate } from '../engine/registry.ts';
import { ERROR_CATALOG, type ErrorCode } from '../schema/errors.ts';
import type { ItemInstance } from '../schema/item.ts';
import type { GradingResult, Response } from '../schema/response.ts';
import { renderComentario } from '../ui/comentario.ts';
import { createPlayer, type ItemPlayer } from '../ui/player.ts';
import { renderRichText } from '../ui/richtext.ts';
import { carregarConfig, EVENTO_MUDOU, montarInstancias } from './config.ts';
import { conta } from '../core/pt.ts';
import { registrarTentativa, tentativaDe } from './historico.ts';
import { codigoDeResultados } from './resultados.ts';
import type { ContextoApp, Vista } from './tipos.ts';

interface Registro {
  readonly instancia: ItemInstance;
  readonly resultado: GradingResult;
  readonly resposta: Response;
  /** Quando foi corrigida — é o que dá ao relatório a ordem e o tempo de cada questão. */
  readonly t: number;
}

/**
 * Quanto tempo o botão do rodapé ignora cliques depois de mudar de função.
 *
 * O mesmo botão diz "Responder" e, corrigida a questão, "Próxima questão". Num duplo
 * clique — hábito de muita gente —, o segundo clique chegava ~150 ms depois do primeiro
 * e já avançava: o aluno não via o nome do erro, que é o retorno inteiro do item.
 */
const TRAVA_DO_DUPLO_CLIQUE_MS = 450;

type Fase = 'inicio' | 'item' | 'fim' | 'revisao';

export function criarVistaPercurso(ctx: ContextoApp): Vista {
  const root = document.createElement('section');
  root.className = 'vista-percurso';

  const palco = document.createElement('div');
  palco.className = 'app-palco';

  const rodape = document.createElement('footer');
  rodape.className = 'app-rodape';
  rodape.hidden = true;
  const retornoCaixa = document.createElement('div');
  const rodapeDentro = document.createElement('div');
  rodapeDentro.className = 'app-rodape-dentro';
  const btnPrincipal = document.createElement('button');
  btnPrincipal.type = 'button';
  btnPrincipal.className = 'app-btn';
  const dicaRodape = document.createElement('span');
  dicaRodape.className = 'app-dica-rodape';
  rodapeDentro.append(btnPrincipal, dicaRodape);
  rodape.append(retornoCaixa, rodapeDentro);

  root.append(palco, rodape);

  const progresso = document.createElement('div');
  progresso.className = 'app-progresso';
  progresso.setAttribute('aria-label', 'Progresso no percurso');

  // --- estado -----------------------------------------------------------------
  let fase: Fase = 'inicio';
  let percurso: ItemInstance[] = [];
  let indice = 0;
  let resultados: Registro[] = [];
  let player: ItemPlayer | null = null;
  let visivel = false;

  function mostrarProgresso(): void {
    if (visivel && (fase === 'item' || fase === 'revisao'))
      ctx.topoExtra.replaceChildren(progresso);
    else ctx.topoExtra.replaceChildren();
  }

  function desenharProgresso(destaque: number): void {
    progresso.replaceChildren(
      ...percurso.map((_, i) => {
        const d = document.createElement('div');
        d.className = 'app-passo';
        const r = resultados[i];
        if (r)
          d.classList.add(
            r.resultado.correct ? 'feito' : r.resultado.score > 0 ? 'parcial' : 'errou',
          );
        else if (i === destaque) d.classList.add('atual');
        if (fase === 'revisao' && i === destaque) d.classList.add('em-revisao');
        return d;
      }),
    );
    mostrarProgresso();
  }

  /** O rodapé fixo cresce com o retorno; o palco precisa de espaço para não ficar sob ele. */
  function ajustarFolga(): void {
    requestAnimationFrame(() => {
      palco.style.paddingBottom = rodape.hidden ? '' : `${rodape.offsetHeight + 36}px`;
    });
  }

  // --- início -----------------------------------------------------------------
  function telaInicial(): void {
    fase = 'inicio';
    player?.dispose();
    player = null;
    rodape.hidden = true;
    ajustarFolga();
    mostrarProgresso();

    const c = carregarConfig();
    const n = c.entradas.length;

    const tela = document.createElement('section');
    tela.className = 'app-tela';

    const h = document.createElement('h1');
    h.textContent =
      n === 0 ? 'Nenhuma questão no percurso' : `Um percurso de ${conta(n, 'questao')}`;

    const p = document.createElement('p');
    p.textContent =
      n === 0
        ? 'Escolha em "Montar percurso" quais itens o aluno vai responder.'
        : 'Cada questão é gerada na hora: números, figuras e posições mudam a cada vez. ' +
          'Depois de responder, você vê o nome do erro — quando houver — e pode abrir um ' +
          'comentário com a sua conta e a conta certa.';
    tela.append(h, p);

    if (n > 0) {
      const ol = document.createElement('ol');
      ol.className = 'app-lista-inicial';
      for (const inst of c.entradas) {
        const li = document.createElement('li');
        li.textContent = tituloDe(inst.templateId);
        ol.appendChild(li);
      }
      tela.appendChild(ol);
    }

    const acoes = document.createElement('div');
    acoes.className = 'app-tela-acoes';
    if (n > 0) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'app-btn';
      b.textContent = 'Começar';
      b.addEventListener('click', comecar);
      acoes.appendChild(b);
    }
    tela.appendChild(acoes);

    // A ÚNICA PORTA LATERAL, e discreta: última linha da página, em texto e sem ícone.
    // "Montar percurso" ficava aqui como botão do mesmo peso de "Começar" — duas ações
    // com o mesmo peso visual, e uma delas não é do aluno.
    const porta = document.createElement('button');
    porta.type = 'button';
    porta.className = 'app-porta';
    porta.textContent = 'Sou professor';
    porta.addEventListener('click', () => ctx.irPara('relatorio'));
    tela.appendChild(porta);

    palco.replaceChildren(tela);
  }

  function comecar(): void {
    percurso = montarInstancias(carregarConfig());
    indice = 0;
    resultados = [];
    if (percurso.length === 0) {
      telaInicial();
      ctx.avisar('Não foi possível gerar as questões agora.');
      return;
    }
    mostrarItem();
  }

  // --- questão ----------------------------------------------------------------
  function mostrarItem(): void {
    fase = 'item';
    player?.dispose();
    retornoCaixa.replaceChildren();
    palco.replaceChildren();

    const instancia = percurso[indice] as ItemInstance;
    player = createPlayer(instancia, {
      mode: 'practice',
      telemetry: ctx.telemetria,
      showRationale: false,
      onGraded: (r, resposta) => aoCorrigir(instancia, r, resposta),
    });
    palco.appendChild(player.element);

    // A ação fica no rodapé fixo: numa prancheta alta, um botão no fim do documento
    // obrigaria o aluno a rolar para longe do desenho só para enviar.
    rodape.hidden = false;
    btnPrincipal.textContent = 'Responder';
    dicaRodape.textContent = `Questão ${indice + 1} de ${percurso.length}`;
    btnPrincipal.onclick = () => player?.responder();

    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    desenharProgresso(indice);
    ajustarFolga();
  }

  function aoCorrigir(instancia: ItemInstance, resultado: GradingResult, resposta: Response): void {
    const t = Date.now();
    resultados[indice] = { instancia, resultado, resposta, t };
    // O histórico alimenta a trilha do mapa e o relatório.
    registrarTentativa(instancia, resultado, t);
    desenharProgresso(-1);

    const caixa = document.createElement('div');
    caixa.className = `app-retorno ${classeDe(resultado)}`;

    // O NOME DO ERRO É O TÍTULO. Um aluno que lê "Média aritmética das velocidades"
    // sabe o que estudar; um aluno que lê "incorreto" só sabe que perdeu ponto.
    const erro = resultado.evidence.matchedError;
    const anchor = erro ? ERROR_CATALOG[erro] : undefined;

    const titulo = document.createElement('p');
    titulo.className = 'app-retorno-titulo';
    titulo.textContent = resultado.correct
      ? 'Correto'
      : anchor
        ? anchor.label
        : resultado.score > 0
          ? `Parcialmente correto — ${Math.round(resultado.score * 100)}%`
          : 'Ainda não';

    // O feedback do corretor começa repetindo o rótulo do erro, porque no laboratório
    // ele aparece sozinho. Aqui o rótulo já é o título: o corpo é a descrição.
    let corpo: HTMLElement;
    if (anchor && !resultado.correct) {
      corpo = document.createElement('p');
      corpo.className = 'rt';
      corpo.textContent = anchor.description;
    } else {
      corpo = renderRichText(resultado.feedback);
    }

    // O comentário detalhado fica logo abaixo da questão, fechado. Abrir é escolha do
    // aluno: quem acertou com segurança não precisa ler a resolução inteira.
    const cartao = cartaoComentario(instancia, resultado, resposta);
    cartao.hidden = true;
    palco.appendChild(cartao);

    const ver = document.createElement('button');
    ver.type = 'button';
    ver.className = 'app-link';
    ver.textContent = resultado.correct ? 'Ver a resolução comentada' : 'Ver comentário detalhado';
    ver.setAttribute('aria-expanded', 'false');
    ver.addEventListener('click', () => {
      cartao.hidden = !cartao.hidden;
      ver.setAttribute('aria-expanded', String(!cartao.hidden));
      ver.textContent = cartao.hidden
        ? resultado.correct
          ? 'Ver a resolução comentada'
          : 'Ver comentário detalhado'
        : 'Ocultar comentário';
      if (!cartao.hidden) cartao.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    caixa.append(titulo, corpo, ver);
    retornoCaixa.replaceChildren(caixa);

    const ultimo = indice === percurso.length - 1;
    btnPrincipal.textContent = ultimo ? 'Ver resultado' : 'Próxima questão';
    const liberaEm = performance.now() + TRAVA_DO_DUPLO_CLIQUE_MS;
    btnPrincipal.onclick = () => {
      if (performance.now() < liberaEm) return;
      if (ultimo) telaFinal();
      else {
        indice += 1;
        mostrarItem();
      }
    };
    ajustarFolga();
  }

  // --- fecho ------------------------------------------------------------------
  function telaFinal(): void {
    fase = 'fim';
    player?.dispose();
    player = null;
    rodape.hidden = true;
    retornoCaixa.replaceChildren();
    ajustarFolga();
    mostrarProgresso();

    const acertos = resultados.filter((r) => r.resultado.correct).length;
    const total = percurso.length;

    const tela = document.createElement('section');
    tela.className = 'app-tela app-tela-fim';
    tela.appendChild(anel(acertos, total));

    const h = document.createElement('h1');
    h.textContent = acertos === total ? 'Percurso completo' : placar(acertos, total);
    const p = document.createElement('p');
    p.textContent =
      acertos === total
        ? 'Nenhum erro diagnosticado. Um percurso novo traz as mesmas questões com outros números e outras figuras.'
        : 'Reveja cada questão do jeito que você respondeu, com o comentário ao lado. Mais abaixo, o que vale estudar.';
    tela.append(h, p);

    // --- cada questão, para rever ---------------------------------------------
    const secao = document.createElement('h2');
    secao.className = 'app-secao';
    secao.textContent = 'Suas respostas';
    const ol = document.createElement('ol');
    ol.className = 'app-respostas';
    resultados.forEach((r, i) => {
      const li = document.createElement('li');
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `app-resposta ${classeDe(r.resultado)}`;

      const status = document.createElement('span');
      status.className = 'app-resposta-status';
      status.textContent = r.resultado.correct ? '✓' : r.resultado.score > 0 ? '◐' : '✕';
      status.setAttribute('aria-hidden', 'true');

      const textoEl = document.createElement('span');
      textoEl.className = 'app-resposta-texto';
      const nome = document.createElement('strong');
      nome.textContent = `${i + 1}. ${r.instancia.title}`;
      const detalhe = document.createElement('span');
      detalhe.textContent = resumoDe(r.resultado);
      textoEl.append(nome, detalhe);

      const acao = document.createElement('span');
      acao.className = 'app-resposta-acao';
      acao.textContent = 'Rever →';

      b.append(status, textoEl, acao);
      b.addEventListener('click', () => revisar(i));
      li.appendChild(b);
      ol.appendChild(li);
    });
    tela.append(secao, ol);

    // --- a pauta de estudo: um item por ERRO distinto --------------------------
    const vistos = new Map<ErrorCode, number>();
    for (const r of resultados) {
      const e = r.resultado.evidence.matchedError;
      if (e) vistos.set(e, (vistos.get(e) ?? 0) + 1);
    }
    if (vistos.size > 0) {
      const t2 = document.createElement('h2');
      t2.className = 'app-secao';
      t2.textContent = 'O que estudar';
      const lista = document.createElement('div');
      lista.className = 'app-revisar';
      for (const [codigo, vezes] of vistos) {
        const a = ERROR_CATALOG[codigo];
        if (!a) continue;
        const card = document.createElement('div');
        card.className = 'app-revisar-item';
        const b = document.createElement('b');
        b.textContent = vezes > 1 ? `${a.label} (${vezes}×)` : a.label;
        const s = document.createElement('span');
        s.textContent = a.description;
        card.append(b, s);
        lista.appendChild(card);
      }
      tela.append(t2, lista);
    }

    // --- enviar ao professor ------------------------------------------------------
    //
    // Um código para copiar, e não um arquivo para baixar: numa página embutida, baixar
    // pode estar bloqueado; copiar texto, nunca.
    const envio = document.createElement('details');
    envio.className = 'app-envio';
    const sumEnvio = document.createElement('summary');
    sumEnvio.textContent = 'Enviar os resultados ao professor';
    const expEnvio = document.createElement('p');
    expEnvio.textContent =
      'Escreva seu nome e copie o código. Ele leva o que você acertou, os erros que cometeu e as dicas que usou — não as suas respostas.';
    const nomeEnvio = document.createElement('input');
    nomeEnvio.type = 'text';
    nomeEnvio.className = 'app-envio-nome';
    nomeEnvio.placeholder = 'Seu nome';
    nomeEnvio.setAttribute('aria-label', 'Seu nome');
    const codigoEnvio = document.createElement('textarea');
    codigoEnvio.className = 'montar-codigo';
    codigoEnvio.readOnly = true;
    codigoEnvio.rows = 3;
    codigoEnvio.setAttribute('aria-label', 'Código de resultado');
    const tentativas = resultados.map((r) => tentativaDe(r.instancia, r.resultado, r.t));
    const atualizarCodigo = (): void => {
      codigoEnvio.value = codigoDeResultados(nomeEnvio.value || 'Sem nome', tentativas);
    };
    nomeEnvio.addEventListener('input', atualizarCodigo);
    codigoEnvio.addEventListener('focus', () => codigoEnvio.select());
    const copiarEnvio = botao('Copiar código', 'app-btn-mini');
    copiarEnvio.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(codigoEnvio.value);
        ctx.avisar('Código copiado. Agora é só enviar ao professor.');
      } catch {
        ctx.avisar('Não deu para copiar sozinho: selecione o código e copie com Ctrl+C.');
      }
    });
    atualizarCodigo();
    envio.append(sumEnvio, expEnvio, nomeEnvio, codigoEnvio, copiarEnvio);
    tela.appendChild(envio);

    const acoes = document.createElement('div');
    acoes.className = 'app-tela-acoes';
    const novo = document.createElement('button');
    novo.type = 'button';
    novo.className = 'app-btn';
    novo.textContent = 'Novo percurso';
    novo.addEventListener('click', comecar);
    const montar = document.createElement('button');
    montar.type = 'button';
    montar.className = 'app-btn app-btn-fantasma';
    montar.textContent = 'Montar percurso';
    montar.addEventListener('click', () => ctx.irPara('montar'));
    acoes.append(novo, montar);
    tela.appendChild(acoes);

    palco.replaceChildren(tela);
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }

  // --- revisão ----------------------------------------------------------------
  function revisar(i: number): void {
    const reg = resultados[i];
    if (!reg) return;
    fase = 'revisao';
    player?.dispose();
    rodape.hidden = true;
    ajustarFolga();

    const nav = document.createElement('div');
    nav.className = 'app-revisao-nav';
    const voltar = botao('← Voltar ao resultado', 'app-link');
    voltar.addEventListener('click', telaFinal);
    const onde = document.createElement('span');
    onde.className = 'app-revisao-onde';
    onde.textContent = `Questão ${i + 1} de ${resultados.length}`;
    const anterior = botao('‹ Anterior', 'app-btn-mini');
    anterior.disabled = i === 0;
    anterior.addEventListener('click', () => revisar(i - 1));
    const proxima = botao('Próxima ›', 'app-btn-mini');
    proxima.disabled = i === resultados.length - 1;
    proxima.addEventListener('click', () => revisar(i + 1));
    const passos = document.createElement('span');
    passos.className = 'app-revisao-passos';
    passos.append(anterior, proxima);
    nav.append(voltar, onde, passos);

    // A questão como foi respondida: a alternativa marcada, o número digitado, a
    // construção feita — com a correção à vista e sem aceitar mudanças.
    player = createPlayer(reg.instancia, {
      mode: 'review',
      review: { response: reg.resposta, result: reg.resultado },
      showRationale: false,
    });

    const veredito = document.createElement('p');
    veredito.className = `app-revisao-veredito ${classeDe(reg.resultado)}`;
    veredito.textContent = resumoDe(reg.resultado);

    palco.replaceChildren(
      nav,
      veredito,
      player.element,
      cartaoComentario(reg.instancia, reg.resultado, reg.resposta),
    );
    desenharProgresso(i);
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }

  /*
   * UM PERCURSO NOVO QUE CHEGA COM A TELA INICIAL ABERTA — um link `#p=…` colado na
   * barra de endereço, o `#demo` antes de apresentar. A casca salvava o percurso e
   * avisava "2 questões", mas a vista já estava à mostra e a tela continuava listando as
   * doze de antes; "Começar" gerava as duas. No meio de um percurso nada muda: o novo
   * vale a partir de "Novo percurso", e ninguém perde as respostas que já deu.
   */
  window.addEventListener(EVENTO_MUDOU, () => {
    if (visivel && fase === 'inicio') telaInicial();
  });

  return {
    element: root,
    mostrar() {
      visivel = true;
      if (fase === 'inicio') telaInicial();
      mostrarProgresso();
    },
    esconder() {
      visivel = false;
      ctx.topoExtra.replaceChildren();
    },
  };
}

// ---------------------------------------------------------------------------

function cartaoComentario(inst: ItemInstance, r: GradingResult, resposta: Response): HTMLElement {
  const cartao = document.createElement('section');
  cartao.className = 'app-comentario';
  const h = document.createElement('h2');
  h.textContent = 'Comentário detalhado';
  cartao.append(h, renderComentario(inst, r, resposta));
  return cartao;
}

function classeDe(r: GradingResult): string {
  return r.correct ? 'ok' : r.score > 0 ? 'parcial' : 'erro';
}

function resumoDe(r: GradingResult): string {
  if (r.correct) return 'Correta';
  const e = r.evidence.matchedError;
  const a = e ? ERROR_CATALOG[e] : undefined;
  if (a) return a.label;
  return r.score > 0 ? `Parcialmente correta — ${Math.round(r.score * 100)}%` : 'Incorreta';
}

/**
 * "3 de 12 questões certas", "1 de 1 questão certa". O título e o rótulo do anel —
 * que o leitor de tela lê — diziam a mesma coisa de dois jeitos, e o do anel saía
 * "1 de 1 questões certas".
 */
function placar(acertos: number, total: number): string {
  return `${acertos} de ${conta(total, 'questao').adj('certo')}`;
}

function tituloDe(templateId: string): string {
  return hasTemplate(templateId) ? getTemplate(templateId).title : templateId;
}

function botao(rotulo: string, cls: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = cls;
  b.textContent = rotulo;
  return b;
}

/** Anel do fecho. Um número grande e um arco; nada mais. */
function anel(acertos: number, total: number): SVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const R = 54;
  const C = 2 * Math.PI * R;
  const fracao = total > 0 ? acertos / total : 0;

  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 132 132');
  svg.setAttribute('class', 'app-anel');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', placar(acertos, total));

  const trilho = document.createElementNS(ns, 'circle');
  trilho.setAttribute('cx', '66');
  trilho.setAttribute('cy', '66');
  trilho.setAttribute('r', String(R));
  trilho.setAttribute('class', 'app-anel-trilho');

  const arco = document.createElementNS(ns, 'circle');
  arco.setAttribute('cx', '66');
  arco.setAttribute('cy', '66');
  arco.setAttribute('r', String(R));
  arco.setAttribute('class', 'app-anel-arco');
  arco.setAttribute('stroke-dasharray', `${C * fracao} ${C}`);
  // Com fração 0 o linecap arredondado ainda pinta um ponto no topo do anel, e esse
  // ponto lê-se como "um acerto".
  if (fracao === 0) arco.setAttribute('visibility', 'hidden');

  const num = document.createElementNS(ns, 'text');
  num.setAttribute('x', '66');
  num.setAttribute('y', '66');
  num.setAttribute('class', 'app-anel-num');
  num.textContent = `${acertos}/${total}`;

  svg.append(trilho, arco, num);
  return svg;
}
